<template>
  <div class="password-step">
    <div class="password-content">
      <h2 class="section-title">Your password must include the following:</h2>
      
      <div v-if="generalError" class="error-message general-error">{{ generalError }}</div>
      
      <div class="password-requirements">
        <div class="requirement" :class="{ 'requirement-met': hasMinLength }">
          <div class="requirement-circle"></div>
          <span>8 characters</span>
        </div>
        <div class="requirement" :class="{ 'requirement-met': hasUppercase }">
          <div class="requirement-circle"></div>
          <span>1 uppercase letter</span>
        </div>
        <div class="requirement" :class="{ 'requirement-met': hasLowercase }">
          <div class="requirement-circle"></div>
          <span>1 lowercase letter</span>
        </div>
        <div class="requirement" :class="{ 'requirement-met': hasSpecialChar }">
          <div class="requirement-circle"></div>
          <span>1 special character</span>
        </div>
        <div class="requirement" :class="{ 'requirement-met': hasNumber }">
          <div class="requirement-circle"></div>
          <span>1 number</span>
        </div>
        <div class="requirement" :class="{ 'requirement-met': passwordsMatch }">
          <div class="requirement-circle"></div>
          <span>Passwords match</span>
        </div>
      </div>
      
      <div class="form-group">
        <label for="password">Enter Password</label>
        <input 
          type="password"
          id="password"
          v-model="password"
          class="form-control"
          placeholder="Enter Password"
        >
      </div>
      
      <div class="form-group">
        <label for="confirmPassword">Confirm Password</label>
        <input 
          type="password"
          id="confirmPassword"
          v-model="confirmPassword"
          class="form-control"
          placeholder="Conform Password"
        >
        <div v-if="error" class="error-message">{{ error }}</div>
      </div>
      
      <div class="action-buttons">
        <button class="btn btn-cancel" @click="handleCancel">Cancel</button>
        <button 
          class="btn btn-submit"
          @click="handleSubmit"
          :disabled="!isPasswordValid || isSubmitting"
        >
          <template v-if="isSubmitting">
            <v-progress-circular indeterminate color="white" size="20" class="spinner" />
          </template>
          <template v-else>
            Submit
          </template>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/apiService';
import { saveAuthToken } from '@/utils/auth';

export default {
  name: 'RegisterStep3',
  props: {
    formData: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      password: '',
      confirmPassword: '',
      isSubmitting: false,
      error: '',
      generalError: ''
    };
  },
  computed: {
    hasMinLength() {
      return this.password.length >= 8;
    },
    hasUppercase() {
      return /[A-Z]/.test(this.password);
    },
    hasLowercase() {
      return /[a-z]/.test(this.password);
    },
    hasSpecialChar() {
      return /[!@#$%^&*(),.?":{}|<>]/.test(this.password);
    },
    hasNumber() {
      return /\d/.test(this.password);
    },
    passwordsMatch() {
      return this.password !== '' && this.password === this.confirmPassword;
    },
    isPasswordValid() {
      return this.hasMinLength && 
             this.hasUppercase && 
             this.hasLowercase && 
             this.hasSpecialChar && 
             this.hasNumber &&
             this.passwordsMatch;
    }
  },
  methods: {
    handleCancel() {
      this.$emit('prev-step');
    },
    async handleSubmit() {
      if (!this.isPasswordValid) {
        if (this.password !== this.confirmPassword) {
          this.error = 'Passwords do not match';
        } else {
          this.error = 'Password does not meet all requirements';
        }
        return;
      }

      this.isSubmitting = true;
      this.error = '';
      this.generalError = '';

      const payload = {
        email: this.formData.email,
        password: this.password,
        confirm_password: this.password
      };

      try {
        if (!apiService || typeof apiService.setPassword !== 'function') {
          throw new Error('apiService.setPassword not available');
        }
        console.log('Calling apiService.setPassword with payload:', payload);
        const response = await apiService.setPassword(payload);
        console.log('Password update success:', response.data);

        if (response.data.token) {
          console.log('Token received in SetPassword step:', response.data.token);
          saveAuthToken(response.data.token);
          this.$store.commit('SET_TOKEN', response.data.token);
        } else {
          console.warn('No token received in SetPassword response.');
        }

        this.$emit('update:form-data', { ...this.formData, password: this.password });
        this.$emit('next-step');
      } catch (error) {
        console.error('apiService.updatePassword failed:', error);

        // Attempt to parse backend validation errors first
        if (error.response && error.response.data) {
          const data = error.response.data;
          if (data.errors && typeof data.errors === 'object') {
            // Reset errors
            this.error = '';
            for (const [field, messages] of Object.entries(data.errors)) {
              const msg = Array.isArray(messages) ? messages.join(', ') : messages;
              if (field === 'password' || field === 'confirm_password') {
                this.error = msg;
              } else {
                this.generalError = msg;
              }
            }
          } else if (data.detail) {
            this.generalError = data.detail;
          }
        }

        try {
          console.log('Falling back to direct axios call for password update...');
          const axios = await import('axios');
          const directResponse = await axios.default.post(
            `/api/business/set-password/`,
            payload,
            {
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              timeout: 10000
            }
          );
          console.log('Direct axios password update success:', directResponse.data);

          if (directResponse.data.token) {
            console.log('Token received in direct setPassword call:', directResponse.data.token);
            saveAuthToken(directResponse.data.token);
            this.$store.commit('SET_TOKEN', directResponse.data.token);
          } else {
            console.warn('No token received in direct setPassword response.');
          }

          this.$emit('update:form-data', { ...this.formData, password: this.password });
          this.$emit('next-step');
        } catch (directError) {
          console.error('Direct axios password update failed:', directError);

          if (directError.response && directError.response.data) {
            const data = directError.response.data;
            if (data.errors && typeof data.errors === 'object') {
              this.error = '';
              for (const [field, messages] of Object.entries(data.errors)) {
                const msg = Array.isArray(messages) ? messages.join(', ') : messages;
                if (field === 'password' || field === 'confirm_password') {
                  this.error = msg;
                } else {
                  this.generalError = msg;
                }
              }
            } else if (data.detail) {
              this.generalError = data.detail;
            } else {
              this.generalError = 'Password update failed. Please try again.';
            }
          } else {
            this.generalError = 'Network error. Please try again.';
          }
        } finally {
          this.isSubmitting = false;
        }
      }
    }
  }
};
</script>

<style scoped>
.password-step {
  width: 100%;
  background: white;
  border-radius: 8px;
  overflow: hidden;
}

.password-content {
  padding: 30px;
}

.section-title {
  font-size: 18px;
  font-weight: normal;
  margin-bottom: 25px;
  color: #333;
}

.password-requirements {
  margin-bottom: 25px;
}

.requirement {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.requirement-circle {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #ccc;
  margin-right: 10px;
}

.requirement-met .requirement-circle {
  background-color: #4A6282;
  border-color: #4A6282;
}

.form-group {
  margin-bottom: 20px;
}

label {
  display: block;
  font-size: 14px;
  margin-bottom: 10px;
  color: #333;
}

.form-control {
  width: 100%;
  padding: 12px 15px;
  font-size: 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #fff;
}

.error-message {
  color: #e94057;
  font-size: 12px;
  margin-top: 5px;
}

.action-buttons {
  display: flex;
  gap: 15px;
}

.btn {
  flex: 1;
  padding: 12px 0;
  font-size: 16px;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid transparent;
  text-align: center;
}

.btn-cancel {
  background-color: #fff;
  color: #333;
  border-color: #ddd;
}

.btn-cancel:hover {
  background-color: #f1f1f1;
}

.btn-submit {
  background-color: #4A6282;
  color: white;
}

.btn-submit:hover {
  background-color: #3d5270;
}

.btn-submit:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.general-error {
  text-align: center;
}

.spinner {
  margin: 0;
}
</style> 